import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getAttachments } from "../../../api/attachments";
import { AttachmentUploadModal } from "../../../components/AttachmentUploadModal";
import { AttachmentManageDialog } from "../../../components/AttachmentManageDialog";
import { InvoiceFormDialog } from "../../../components/Settings/InvoiceFormDialog";
import { useAddInvoice } from "../../../api/lists";
import type { AttachmentDto, NewInvoiceEntity } from "../../../api/types";
import { filterAttachments } from "./filterAttachments";
import { defaultFilters, type Filters } from "./types";
import { AttachmentFilters } from "./components/AttachmentFilters";
import {
  ViewModeToggle,
  type ViewMode,
} from "../../../components/ViewModeToggle";
import {
  AdminListBody,
  AdminListPageHeader,
} from "../../../components/AdminList";
import { AttachmentListView } from "./components/AttachmentListView";
import { AttachmentTileView } from "./components/AttachmentTileView";

const VIEW_MODE_KEY = "attachments.viewMode";

const readStoredViewMode = (): ViewMode => {
  if (typeof window === "undefined") return "list";
  const stored = window.localStorage.getItem(VIEW_MODE_KEY);
  return stored === "tile" ? "tile" : "list";
};

export const AttachmentsPage = () => {
  const queryClient = useQueryClient();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState<AttachmentDto | null>(null);
  const [createInvoiceFor, setCreateInvoiceFor] =
    useState<AttachmentDto | null>(null);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    readStoredViewMode(),
  );

  const addInvoice = useAddInvoice();

  const handleViewModeChange = (next: ViewMode) => {
    setViewMode(next);
    window.localStorage.setItem(VIEW_MODE_KEY, next);
  };

  const closeInvoiceForm = () => setCreateInvoiceFor(null);

  // Throws on failure: the form shows the error itself, and turns a duplicate
  // refusal into its open / discard / create-anyway dialog.
  const handleCreateInvoice = async (
    entity: NewInvoiceEntity,
    { force }: { force: boolean },
  ) => {
    await addInvoice.mutateAsync({ invoice: entity, force });
    closeInvoiceForm();
  };

  const { data: attachments, isLoading } = useQuery<AttachmentDto[]>({
    queryKey: ["attachments"],
    queryFn: getAttachments,
  });

  const filtered = useMemo(
    () => (attachments ? filterAttachments(attachments, filters) : []),
    [attachments, filters],
  );

  const total = attachments?.length ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <AdminListPageHeader
        title="Attachments"
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setIsUploadModalOpen(true)}
            >
              Upload Attachment
            </Button>
          </>
        }
      />

      <AttachmentFilters value={filters} onChange={setFilters} />

      <AdminListBody
        isLoading={isLoading}
        total={total}
        filteredCount={filtered.length}
        noun="attachment"
      >
        {viewMode === "list" ? (
          <AttachmentListView
            attachments={filtered}
            onManage={setManageTarget}
            onCreateInvoice={setCreateInvoiceFor}
          />
        ) : (
          <AttachmentTileView
            attachments={filtered}
            onManage={setManageTarget}
            onCreateInvoice={setCreateInvoiceFor}
          />
        )}
      </AdminListBody>

      <AttachmentManageDialog
        key={manageTarget?.id ?? "closed"}
        open={manageTarget !== null}
        attachment={manageTarget}
        onClose={() => setManageTarget(null)}
      />
      <AttachmentUploadModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={() => {
          setIsUploadModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["attachments"] });
        }}
      />
      {createInvoiceFor && (
        <InvoiceFormDialog
          open
          title="Create invoice from attachment"
          onClose={closeInvoiceForm}
          onSave={handleCreateInvoice}
          prefilledAttachmentId={createInvoiceFor.id}
        />
      )}
    </Box>
  );
};
